import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const formatNumber = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN").format(num);
};

const formatINR = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(num);
};



const StatCard = ({ title, value, sub }) => (
  <div className="col-12 col-sm-6 col-lg-4 col-xl-2" style={{ marginBottom: 12 }}>
    <div className="card" style={{ height: "100%" }}>
      <div className="card-body" style={{ padding: 14 }}>
        <div className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
          {title}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, marginTop: 6 }}>{value}</div>
        {sub ? (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

const AdminReport = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => localStorage.getItem("userid"), []);

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Left table paging
  const [adminLimit, setAdminLimit] = useState(25);
  const [adminPage, setAdminPage] = useState(1);

  // Right table paging (tenants)
  const [tenantLimit, setTenantLimit] = useState(25);
  const [tenantPage, setTenantPage] = useState(1);

  const getId = (x) => x?._id || x?.id || x?.userid;

  const normalizeResponse = (data) => {
    // New backend shape: returns a plain array of admin objects
    if (Array.isArray(data)) {
      return {
        status: "success",
        message: "Admins retrieved",
        totalAdmins: data.length,
        totalTenants: 0,
        totalProperties: 0,
        monthlyRevenue: 0,
        totalRevenue: 0,
        recentAdmins: data,
        recentTenants: [],
      };
    }

    // Some APIs return { status, data: [...] }
    if (Array.isArray(data?.data)) {
      return {
        status: data?.status || "success",
        message: data?.message || "Admins retrieved",
        totalAdmins: data.data.length,
        totalTenants: 0,
        totalProperties: 0,
        monthlyRevenue: 0,
        totalRevenue: 0,
        recentAdmins: data.data,
        recentTenants: [],
      };
    }

    // Old backend shape: analytics object
    if (data && typeof data === "object" && Array.isArray(data.admins)) {
      return {
        status: data.status || "success",
        message: data.message || "System analytics retrieved",
        totalAdmins: Number(data.totalAdmins ?? data.admins.length ?? 0),
        totalTenants: Number(data.totalTenants ?? 0),
        totalProperties: Number(data.totalProperties ?? 0),
        monthlyRevenue: Number(data.monthlyRevenue ?? 0),
        totalRevenue: Number(data.totalRevenue ?? 0),
        admins: data.admins,
      };
    }

    return data;
  };

  const fetchAnalytics = async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setLoading(true);
    let didIncrement = false;
    try {
      const payload = {
        action: "superadmin.getSystemAnalytics",
        userId,
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const raw = response?.data;
      const data = normalizeResponse(raw);

      const adminsArr = Array.isArray(data?.admins)
        ? data.admins
        : Array.isArray(data?.recentAdmins)
          ? data.recentAdmins
          : [];

      if (data?.status === "success" || Array.isArray(data?.admins)) {
        setAnalytics(data);
        // auto-select first admin if nothing selected (or selected admin no longer exists)
        if (adminsArr.length) {
          setSelectedAdmin((prev) => {
            if (!prev) return adminsArr[0];
            const prevId = getId(prev);
            const stillThere = adminsArr.some((a) => getId(a) === prevId);
            return stillThere ? prev : adminsArr[0];
          });
        } else {
          setSelectedAdmin(null);
        }
      } else {
        toast.error(data?.message || "Failed to load analytics");
        setAnalytics(null);
        setSelectedAdmin(null);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      setAnalytics(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAdminDetails = async (adminRow) => {
    const adminId = getId(adminRow);
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }
    if (!adminId) {
      toast.error("Missing adminId");
      return;
    }

    // keep row highlight immediately
    setSelectedAdmin(adminRow);
    // reset tenant pagination when selecting new admin
    setTenantPage(1);

    setDetailsLoading(true);
    let didIncrement = false;
    try {
      const payload = {
        action: "superadmin.getAdminDetails",
        userId,
        adminId,
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if ((data?.status === "success" || data?.success === true) && data?.admin) {
        setSelectedAdmin(data.admin);
      } else {
        toast.error(data?.message || "Failed to load admin details");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setDetailsLoading(false);
    }
  };

  const admins = Array.isArray(analytics?.admins)
    ? analytics.admins
    : Array.isArray(analytics?.recentAdmins)
      ? analytics.recentAdmins
      : [];

  const totalAdminPages = Math.max(1, Math.ceil((admins.length || 0) / adminLimit));
  const safeAdminPage = Math.min(Math.max(1, adminPage), totalAdminPages);
  const adminStart = (safeAdminPage - 1) * adminLimit;
  const adminRows = admins.slice(adminStart, adminStart + adminLimit);

  // Tenant pagination
  const tenants = Array.isArray(selectedAdmin?.tenants) ? selectedAdmin.tenants : [];
  const totalTenantPages = Math.max(1, Math.ceil((tenants.length || 0) / tenantLimit));
  const safeTenantPage = Math.min(Math.max(1, tenantPage), totalTenantPages);
  const tenantStart = (safeTenantPage - 1) * tenantLimit;
  const tenantRows = tenants.slice(tenantStart, tenantStart + tenantLimit);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-end flex-wrap" style={{ gap: 12 }}>
          <div>
            <h1 className="m-0 txt_lft">Admin Report</h1>
            <p style={{ margin: 0 }}>System analytics overview.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={fetchAnalytics} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <StatCard title="Total Admins" value={formatNumber(analytics?.totalAdmins)} />
            <StatCard title="Total Tenants" value={formatNumber(analytics?.totalTenants)} />
            <StatCard title="Total Properties" value={formatNumber(analytics?.totalProperties)} />
            <StatCard title="Monthly Revenue" value={formatINR(analytics?.monthlyRevenue)} />
            <StatCard title="Total Revenue" value={formatINR(analytics?.totalRevenue)} />
          </div>

          <div className="row admin-report-tables">
            {/* Admin master list */}
            <div className="col-12 col-lg-5" style={{ marginBottom: 12 }}>
              <div className="card">
                <div className="card-header">
                  Admins
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>SI.No</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th style={{ width: 80, textAlign: "center" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminRows.length ? (
                          adminRows.map((a, idx) => {
                            const isSelected = getId(selectedAdmin) && getId(a) === getId(selectedAdmin);
                            return (
                              <tr
                                key={`${getId(a) || a?.email || "admin"}-${idx}`}
                                style={{ background: isSelected ? "rgba(2,132,199,0.08)" : undefined }}
                              >
                                <td>{adminStart + idx + 1}</td>
                                <td>{a?.name || "-"}</td>
                                <td>{a?.email || "-"}</td>
                                <td style={{ textAlign: "center" }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    title="Open"
                                    onClick={() => fetchAdminDetails(a)}
                                    style={{ width: 34, height: 34, borderRadius: 8 }}
                                  >
                                    <i className="fa-solid fa-angle-right" aria-hidden="true" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center text-muted">
                              No admins
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Show:
                      </span>
                      <select
                        className="form-control"
                        style={{ width: 90 }}
                        value={adminLimit}
                        onChange={(e) => {
                          setAdminPage(1);
                          setAdminLimit(Number(e.target.value));
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>

                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={safeAdminPage <= 1}
                        onClick={() => setAdminPage(1)}
                      >
                        &laquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={safeAdminPage <= 1}
                        onClick={() => setAdminPage((p) => Math.max(1, p - 1))}
                      >
                        &lsaquo;
                      </button>
                      <button type="button" className="btn btn-primary" disabled>
                        {safeAdminPage}
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={safeAdminPage >= totalAdminPages}
                        onClick={() => setAdminPage((p) => Math.min(totalAdminPages, p + 1))}
                      >
                        &rsaquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={safeAdminPage >= totalAdminPages}
                        onClick={() => setAdminPage(totalAdminPages)}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected admin's users table */}
            <div className="col-12 col-lg-7" style={{ marginBottom: 12 }}>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  {selectedAdmin?.name ? `${selectedAdmin.name}'s Users` : "Admin Users"}
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    {selectedAdmin?.phone && (
                      <span className="admin-phone-display">
                        {selectedAdmin.phone}
                      </span>
                    )}
                    {selectedAdmin?.stats?.tenantCount !== undefined && (
                      <div className="tenant-count-badge">
                        Tenant: {formatNumber(selectedAdmin.stats.tenantCount)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  {detailsLoading ? (
                    <div className="text-center text-muted" style={{ padding: 24 }}>
                      Loading admin users...
                    </div>
                  ) : selectedAdmin ? (
                    <>
                      {/* Tenants table */}
                      {Array.isArray(selectedAdmin?.tenants) ? (
                        <>
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th style={{ width: 60 }}>SI.No</th>
                                  <th>Name</th>
                                  <th>Email</th>
                                  <th style={{ width: 90 }}>Flat</th>
                                  <th style={{ width: 100 }}>Role</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tenantRows.length ? (
                                  tenantRows.map((t, idx) => (
                                    <tr key={`${getId(t) || t?.email || "tenant"}-${idx}`}>
                                      <td>{tenantStart + idx + 1}</td>
                                      <td>{t?.name || "-"}</td>
                                      <td>{t?.email || "-"}</td>
                                      <td>{t?.flatNumber || "-"}</td>
                                      <td>
                                        <span
                                          className="badge"
                                          style={{
                                            padding: "0.25rem 0.5rem",
                                            borderRadius: 12,
                                            fontWeight: 500,
                                            fontSize: 11,
                                            background: t?.role === "TENANT" ? "rgba(34,197,94,0.15)" : 
                                                       t?.role === "WATCHMAN" ? "rgba(59,130,246,0.15)" : 
                                                       "rgba(107,114,128,0.15)",
                                            color: t?.role === "TENANT" ? "#16a34a" : 
                                                  t?.role === "WATCHMAN" ? "#2563eb" : 
                                                  "#374151",
                                          }}
                                        >
                                          {t?.role || "USER"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={5} className="text-center text-muted">
                                      No tenants
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Tenant pagination */}
                          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                              <span className="text-muted" style={{ fontSize: 12 }}>
                                Show:
                              </span>
                              <select
                                className="form-control"
                                style={{ width: 90 }}
                                value={tenantLimit}
                                onChange={(e) => {
                                  setTenantPage(1);
                                  setTenantLimit(Number(e.target.value));
                                }}
                              >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                              </select>
                            </div>

                            <div className="btn-group">
                              <button
                                type="button"
                                className="btn btn-light"
                                disabled={safeTenantPage <= 1}
                                onClick={() => setTenantPage(1)}
                              >
                                &laquo;
                              </button>
                              <button
                                type="button"
                                className="btn btn-light"
                                disabled={safeTenantPage <= 1}
                                onClick={() => setTenantPage((p) => Math.max(1, p - 1))}
                              >
                                &lsaquo;
                              </button>
                              <button type="button" className="btn btn-primary" disabled>
                                {safeTenantPage}
                              </button>
                              <button
                                type="button"
                                className="btn btn-light"
                                disabled={safeTenantPage >= totalTenantPages}
                                onClick={() => setTenantPage((p) => Math.min(totalTenantPages, p + 1))}
                              >
                                &rsaquo;
                              </button>
                              <button
                                type="button"
                                className="btn btn-light"
                                disabled={safeTenantPage >= totalTenantPages}
                                onClick={() => setTenantPage(totalTenantPages)}
                              >
                                &raquo;
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center text-muted" style={{ padding: 24 }}>
                          No tenant data available
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted" style={{ padding: 24 }}>
                      Select an admin from the left table to view users.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReport;